import React, { useState } from "react";
import { Box, Stepper, Step, StepLabel, Paper, Typography } from "@mui/material";
import InformantDetails from "./informant-details/InformantDetails";
import DeceasedDetails from "./deceased-details/deceasedDetails";
import { StepIconProps } from "@mui/material/StepIcon";
import { styled } from "@mui/material/styles";
import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";
import RequestServices from "./request-services/RequestServices";
import BodyLocation from "./body-location/BodyLocation";
import CaseDocuments from "./case-documents/CaseDocuments";
import { useAuthStore } from "../../../store/useAuthStore";
import { useCaseCreationStore } from "../../../store/useCaseCreationStore";
import Documents from "../components/forms/Documents";
import { string } from "../../../constants/string";
import CaseSubmission from "./case-submission/CaseSubmission";
import { useUserStore } from "../../../store/useUserStore";
import { useSessionStore } from "../../../store/useSessionStore";
import { isRemovalCaseInitiatorRole } from "../../../utils/ifasAccess";
import RemovalDeceasedLocation from "./removal-stage/RemovalDeceasedLocation";

const steps = [
  "Informant Details",
  "Deceased Details",
  "Request Services",
  "Body Location",
  "Documents",
];

const CustomConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 12,
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor: "#E6E8EF",
    borderRadius: 999,
  },
  [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
    backgroundColor: "#F97316", // orange
  },
  [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
    backgroundColor: "#F97316",
  },
}));

function CustomStepIcon(props: StepIconProps) {
  const { active, completed } = props;

  return (
    <Box
      sx={{
        width: 18,
        height: 18,
        borderRadius: "50%",
        backgroundColor: active || completed ? "#F97316" : "#D1D5DB",
        border: active ? "4px solid #FED7AA" : "none",
        boxShadow: active ? "0 0 0 4px rgba(249, 115, 22, 0.12)" : "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {active && (
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: "#fff",
          }}
        />
      )}
    </Box>
  );
}

export default function CreateCase({ onSubmit }: any) {
  const [activeStep, setActiveStep] = useState(0);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const role = useUserStore((state) => state.role);
  const sessionRole = useSessionStore((state) => state.userRole);
  const initialCapture = isRemovalCaseInitiatorRole(sessionRole || role);
  const visibleSteps = initialCapture
    ? ["Informant Details", "Deceased & Body Location"]
    : steps;
  const { informantDetails } = useCaseCreationStore();

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const onHandleSubmit = () => {
    onSubmit();
  };

  const renderStepContent = () => {
    if (activeStep >= visibleSteps.length) {
      return (
        <CaseSubmission
          heading={string.case.heading}
          para={initialCapture
            ? "The initial case has been saved and is ready for planner allocation."
            : string.case.para}
          onHomeClick={isAuthenticated ? onHandleSubmit : null}
        />
      );
    }

    switch (visibleSteps[activeStep]) {
      case "Informant Details":
        return <InformantDetails onNext={handleNext} minimalCapture={initialCapture} />;
      case "Deceased Details":
        return <DeceasedDetails onNext={handleNext} onBack={handleBack} minimalCapture={initialCapture} />;
      case "Deceased & Body Location":
        return <RemovalDeceasedLocation onNext={handleNext} onBack={handleBack} />;
      case "Request Services":
        return <RequestServices onNext={handleNext} onBack={handleBack} />;
      case "Body Location":
        return <BodyLocation onNext={handleNext} onBack={handleBack} minimalCapture={initialCapture} />;
      case "Documents":
      case "Review & Save":
        return <CaseDocuments onBack={handleBack} onNext={handleNext} minimalCapture={initialCapture} />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* Stepper */}
      {activeStep < visibleSteps.length && (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 2.5 },
            borderRadius: 2,
            border: "1px solid #E6E8EF",
            backgroundColor: "#FFFFFF",
            boxShadow: "0 8px 22px rgba(17, 24, 39, 0.045)",
          }}
        >
          <Box
            display="flex"
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            gap={2}
            mb={2}
            flexDirection={{ xs: "column", sm: "row" }}
          >
            <Typography fontSize={22} fontWeight={800} textAlign="left">
              New Case
            </Typography>
            <Typography fontSize={13} fontWeight={700} color="text.disabled">
              Step {activeStep + 1} of {visibleSteps.length}
            </Typography>
          </Box>
          <Box sx={{ overflowX: "auto", pb: 0.5 }}>
            <Stepper
              activeStep={activeStep}
              alternativeLabel
              connector={<CustomConnector />}
              sx={{
                minWidth: { xs: 720, md: "auto" },
              }}
            >
              {visibleSteps.map((label, index) => (
                <Step key={label}>
                  <StepLabel
                    StepIconComponent={CustomStepIcon}
                    sx={{
                      "& .MuiStepLabel-label": {
                        mt: 0.75,
                        fontWeight: 800,
                        fontSize: 13,
                        color: activeStep === index ? "#F97316" : "#475467",
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        </Paper>
      )}

      {renderStepContent()}
    </Box>
  );
}
